import { useRef, useState, useEffect, useCallback } from 'react';
import { Box, Container, Grid, Typography, Chip, Stack, Button, CircularProgress, IconButton, Dialog } from '@mui/material';
import { useParams, Link as RouterLink, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CTABanner from '../components/common/CTABanner';
import { useProject } from '../hooks/usePublicApi';
import { projects as staticProjects } from '../data/projects';
import { getImageUrl } from '../utils/imageUrl';

const navy  = '#0F1D2E';
const navyL = '#1B2D42';
const gold  = '#D1BC8A';
const goldD = '#B8A06E';

const catLabels = { neubau: 'Neubau', umbau: 'Umbau', sanierung: 'Sanierung' };

// Blueprint grid overlay
function Blueprint({ opacity = 0.055 }) {
  return (
    <Box sx={{
      position: 'absolute', inset: 0, pointerEvents: 'none',
      backgroundImage: `
        linear-gradient(rgba(255,255,255,${opacity}) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,${opacity}) 1px, transparent 1px)
      `,
      backgroundSize: '36px 36px',
    }} />
  );
}

// Scroll-triggered fade-up
function InView({ children, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px 0px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

// ── Fullscreen image lightbox ──────────────────────────────────────────────────
function Lightbox({ images, startIndex, onClose }) {
  const [idx, setIdx] = useState(startIndex);

  const prev = useCallback(() => setIdx(i => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIdx(i => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowLeft')  prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape')     onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [prev, next, onClose]);

  const img = images[idx];
  const src = getImageUrl(img.filename, img.folder || 'projects');

  return (
    <Dialog
      open
      onClose={onClose}
      fullScreen
      PaperProps={{ sx: { bgcolor: 'rgba(5,10,18,0.97)', position: 'relative' } }}
    >
      {/* Close */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute', top: 16, right: 16, zIndex: 10,
          bgcolor: 'rgba(255,255,255,0.1)', color: '#fff',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.15)',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Counter */}
      <Box sx={{
        position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 10,
        bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)',
        border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: '20px', px: 2, py: 0.5,
        color: 'rgba(255,255,255,0.8)', fontSize: '0.82rem', fontWeight: 600,
        userSelect: 'none',
      }}>
        {idx + 1} / {images.length}
      </Box>

      {/* Main image area */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100%', px: { xs: 6, md: 10 },
      }}>
        <AnimatePresence mode="wait">
          <motion.img
            key={idx}
            src={src}
            alt={img.alt_text || `Bild ${idx + 1}`}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.02 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            style={{
              maxWidth: '100%', maxHeight: '85vh',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 24px 80px rgba(0,0,0,0.7)',
              display: 'block',
            }}
          />
        </AnimatePresence>
      </Box>

      {/* Alt text caption */}
      {img.alt_text && (
        <Box sx={{
          position: 'absolute', bottom: 28, left: '50%', transform: 'translateX(-50%)',
          color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', textAlign: 'center',
          maxWidth: 480, px: 2,
        }}>
          {img.alt_text}
        </Box>
      )}

      {/* Prev arrow */}
      {images.length > 1 && (
        <IconButton
          onClick={prev}
          sx={{
            position: 'absolute', left: { xs: 8, md: 20 }, top: '50%', transform: 'translateY(-50%)',
            bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: '#fff', width: 48, height: 48,
            '&:hover': { bgcolor: `rgba(209,188,138,0.25)`, borderColor: gold },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 28 }} />
        </IconButton>
      )}

      {/* Next arrow */}
      {images.length > 1 && (
        <IconButton
          onClick={next}
          sx={{
            position: 'absolute', right: { xs: 8, md: 20 }, top: '50%', transform: 'translateY(-50%)',
            bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: '#fff', width: 48, height: 48,
            '&:hover': { bgcolor: `rgba(209,188,138,0.25)`, borderColor: gold },
          }}
        >
          <ChevronRightIcon sx={{ fontSize: 28 }} />
        </IconButton>
      )}

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <Box sx={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          display: 'flex', justifyContent: 'center', gap: 1,
          p: 2, bgcolor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(10px)',
          overflowX: 'auto',
        }}>
          {images.map((im, i) => (
            <Box
              key={i}
              onClick={() => setIdx(i)}
              sx={{
                width: 56, height: 40, flexShrink: 0,
                borderRadius: '6px', overflow: 'hidden', cursor: 'pointer',
                border: `2px solid ${i === idx ? gold : 'transparent'}`,
                opacity: i === idx ? 1 : 0.5,
                transition: 'all 0.2s ease',
                '&:hover': { opacity: 1 },
              }}
            >
              <Box
                component="img"
                src={getImageUrl(im.filename, im.folder || 'projects')}
                alt=""
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </Box>
          ))}
        </Box>
      )}
    </Dialog>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const { project: apiProject, loading } = useProject(id);
  const project = apiProject || staticProjects.find((p) => p.id === id);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: gold }} />
      </Box>
    );
  }

  if (!project) return <Navigate to="/referenzen" replace />;

  // Cover image for the visual banner (separate from gallery)
  const coverUrl = project.filename
    ? getImageUrl(project.filename, project.folder || 'projects')
    : (project.images?.length > 0 ? getImageUrl(project.images[0].filename, 'projects') : null);

  // Gallery = ALL images in the images array
  const galleryImages = project.images || [];

  const gradient = project.gradient || `linear-gradient(135deg, ${navyL} 0%, #2A4562 100%)`;
  const categoryLabel = catLabels[project.category] || project.category;

  return (
    <>
      <Helmet>
        <title>{project.title} — Referenz | Süd Ausbau GmbH</title>
        <meta name="description" content={project.description} />
      </Helmet>

      {/* ── Hero banner with title ───────────────────────────────────────────── */}
      <Box sx={{
        position: 'relative', overflow: 'hidden',
        height: { xs: 160, sm: 200, md: 260 },
      }}>
        {/* Background */}
        <Box sx={{
          position: 'absolute', inset: 0,
          background: coverUrl ? `url(${coverUrl}) center/cover no-repeat` : gradient,
        }} />

        <Blueprint />

        {/* Top dark scrim (for back button legibility) */}
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, height: '35%',
          background: 'linear-gradient(to bottom, rgba(15,29,46,0.72) 0%, transparent 100%)',
        }} />

        {/* Bottom scrim (for title legibility) */}
        <Box sx={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: '65%',
          background: 'linear-gradient(to top, rgba(15,29,46,0.95) 0%, rgba(15,29,46,0.4) 60%, transparent 100%)',
        }} />

        {/* Diagonal light sweep */}
        <Box sx={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'linear-gradient(120deg, transparent 40%, rgba(255,255,255,0.03) 52%, transparent 62%)',
        }} />

        {/* Corner tick marks */}
        {[
          { top: 16, left: 16, borderTop: '2px solid', borderLeft: '2px solid' },
          { top: 16, right: 16, borderTop: '2px solid', borderRight: '2px solid' },
          { bottom: 16, left: 16, borderBottom: '2px solid', borderLeft: '2px solid' },
          { bottom: 16, right: 16, borderBottom: '2px solid', borderRight: '2px solid' },
        ].map((s, i) => (
          <Box key={i} sx={{ position: 'absolute', width: 20, height: 20, borderColor: 'rgba(209,188,138,0.35)', ...s }} />
        ))}

        {/* Back button */}
        <Box sx={{ position: 'absolute', top: 18, left: { xs: 16, md: 32 }, zIndex: 2 }}>
          <Button
            component={RouterLink}
            to="/referenzen"
            startIcon={<ArrowBackIcon sx={{ fontSize: '16px !important' }} />}
            sx={{
              color: 'rgba(255,255,255,0.85)',
              bgcolor: 'rgba(0,0,0,0.28)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.16)',
              borderRadius: '10px', px: 2, py: 0.7,
              fontSize: '0.78rem', fontWeight: 600, textTransform: 'none',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.45)', color: '#fff' },
            }}
          >
            Referenzen
          </Button>
        </Box>

        {/* Category chip */}
        <Box sx={{ position: 'absolute', top: 18, right: { xs: 16, md: 32 }, zIndex: 2 }}>
          <Chip
            label={categoryLabel}
            size="small"
            sx={{
              bgcolor: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(8px)',
              color: navy, fontWeight: 700, textTransform: 'capitalize',
              fontSize: '0.72rem', boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
            }}
          />
        </Box>

        {/* Bottom: title + metadata */}
        <Container maxWidth="lg" sx={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', zIndex: 2 }}>
          <Box sx={{ pb: { xs: 1.5, md: 2 } }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* Overline */}
              <Typography sx={{
                color: gold, fontWeight: 700, fontSize: '0.7rem',
                textTransform: 'uppercase', letterSpacing: '0.22em', mb: 1,
              }}>
                {categoryLabel}
              </Typography>

              {/* Title */}
              <Typography
                component="h1"
                sx={{
                  color: '#fff', fontWeight: 900,
                  fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' },
                  lineHeight: 1.1, letterSpacing: '-0.025em', mb: 1,
                }}
              >
                {project.title}
              </Typography>

              {/* Metadata row */}
              <Stack direction="row" spacing={3} flexWrap="wrap">
                {project.location && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <LocationOnIcon sx={{ fontSize: 13, color: gold }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>{project.location}</Typography>
                  </Stack>
                )}
                {project.year && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <CalendarTodayIcon sx={{ fontSize: 12, color: gold }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>{project.year}</Typography>
                  </Stack>
                )}
                {project.duration && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <AccessTimeIcon sx={{ fontSize: 13, color: gold }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>{project.duration}</Typography>
                  </Stack>
                )}
              </Stack>
            </motion.div>
          </Box>
        </Container>
      </Box>

      {/* ── Content + sidebar ───────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 7, md: 10 }, bgcolor: '#F8F7F5' }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 6 }}>

            {/* Main content */}
            <Grid size={{ xs: 12, md: 8 }}>

              {/* Image gallery — ALL images, each clickable */}
              {galleryImages.length > 0 && (
                <InView>
                  <Typography variant="h6" sx={{ color: navy, fontWeight: 700, mb: 2 }}>
                    Projektfotos
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 5 }}>
                    {galleryImages.map((img, idx) => (
                      <Grid size={{ xs: 12, sm: 6 }} key={idx}>
                        <Box
                          onClick={() => setLightboxIndex(idx)}
                          sx={{
                            width: '100%', paddingTop: '62%', position: 'relative',
                            borderRadius: '14px', overflow: 'hidden',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                            cursor: 'pointer',
                            '&:hover .zoom-overlay': { opacity: 1 },
                            '&:hover img': { transform: 'scale(1.05)' },
                          }}
                        >
                          <Box
                            component="img"
                            src={getImageUrl(img.filename, img.folder || 'projects')}
                            alt={img.alt_text || project.title}
                            sx={{
                              position: 'absolute', inset: 0, width: '100%', height: '100%',
                              objectFit: 'cover',
                              transition: 'transform 0.45s ease',
                              display: 'block',
                            }}
                          />
                          {/* Hover overlay */}
                          <Box
                            className="zoom-overlay"
                            sx={{
                              position: 'absolute', inset: 0,
                              background: 'rgba(15,29,46,0.45)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              opacity: 0, transition: 'opacity 0.3s ease',
                            }}
                          >
                            <Box sx={{
                              width: 44, height: 44, borderRadius: '50%',
                              bgcolor: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)',
                              border: '1px solid rgba(255,255,255,0.3)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <ZoomInIcon sx={{ color: '#fff', fontSize: 22 }} />
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </InView>
              )}

              <InView>
                <Typography sx={{ color: gold, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.2em', mb: 1 }}>
                  Projektbeschreibung
                </Typography>
                <Typography variant="h4" sx={{ color: navy, fontWeight: 800, mb: 2.5, lineHeight: 1.2, letterSpacing: '-0.02em' }}>
                  {project.title}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85, fontSize: '1rem', mb: 4 }}>
                  {project.description}
                </Typography>
              </InView>

              {project.details?.length > 0 && (
                <InView delay={0.1}>
                  <Typography variant="h6" sx={{ color: navy, fontWeight: 700, mb: 2.5 }}>
                    Leistungen im Überblick
                  </Typography>
                  <Grid container spacing={1.5}>
                    {project.details.map((detail, i) => (
                      <Grid size={{ xs: 12, sm: 6 }} key={i}>
                        <Stack
                          direction="row" spacing={1.5} alignItems="center"
                          sx={{
                            p: 1.75, bgcolor: '#fff', borderRadius: '12px',
                            border: '1px solid rgba(15,29,46,0.07)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                            transition: 'all 0.2s ease',
                            '&:hover': { borderColor: 'rgba(209,188,138,0.4)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' },
                          }}
                        >
                          <Box sx={{
                            width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                            background: `linear-gradient(135deg, ${gold} 0%, ${goldD} 100%)`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <CheckIcon sx={{ fontSize: 14, color: navy, fontWeight: 900 }} />
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: navy, lineHeight: 1.45 }}>
                            {typeof detail === 'string' ? detail : detail.detail_text}
                          </Typography>
                        </Stack>
                      </Grid>
                    ))}
                  </Grid>
                </InView>
              )}
            </Grid>

            {/* Sticky sidebar */}
            <Grid size={{ xs: 12, md: 4 }}>
              <InView delay={0.15}>
                <Box sx={{
                  background: `linear-gradient(145deg, ${navy} 0%, ${navyL} 100%)`,
                  borderRadius: '20px', p: { xs: 3, md: 3.5 },
                  position: { md: 'sticky' }, top: { md: 96 },
                  boxShadow: '0 12px 40px rgba(15,29,46,0.2)',
                }}>
                  <Typography sx={{
                    color: gold, fontWeight: 700, fontSize: '0.68rem',
                    textTransform: 'uppercase', letterSpacing: '0.2em', mb: 3,
                  }}>
                    Projektdaten
                  </Typography>

                  <Stack spacing={2.5} sx={{ mb: 3.5 }}>
                    {[
                      { Icon: LocationOnIcon, label: 'Standort',  value: project.location },
                      { Icon: CalendarTodayIcon, label: 'Jahr',   value: project.year },
                      { Icon: AccessTimeIcon, label: 'Dauer',     value: project.duration },
                    ].filter((item) => item.value).map(({ Icon, label, value }) => (
                      <Stack key={label} direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{
                          width: 38, height: 38, borderRadius: '10px', flexShrink: 0,
                          bgcolor: 'rgba(209,188,138,0.12)',
                          border: '1px solid rgba(209,188,138,0.2)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Icon sx={{ fontSize: 18, color: gold }} />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.42)', display: 'block', mb: 0.1 }}>
                            {label}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                            {value}
                          </Typography>
                        </Box>
                      </Stack>
                    ))}
                  </Stack>

                  <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.08)', pt: 3 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.48)', mb: 2.5, lineHeight: 1.7, fontSize: '0.83rem' }}>
                      Ähnliches Projekt geplant? Wir beraten Sie gerne — kostenlos und unverbindlich.
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      component={RouterLink}
                      to="/anfrage"
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        background: `linear-gradient(135deg, ${gold} 0%, ${goldD} 100%)`,
                        color: navy, fontWeight: 800,
                        borderRadius: '12px', py: 1.4,
                        boxShadow: `0 6px 20px rgba(209,188,138,0.28)`,
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 12px 32px rgba(209,188,138,0.38)` },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Anfrage senden
                    </Button>
                  </Box>
                </Box>
              </InView>
            </Grid>

          </Grid>
        </Container>
      </Box>

      <CTABanner pageKey="referenzen" />

      {/* Lightbox */}
      {lightboxIndex !== null && galleryImages.length > 0 && (
        <Lightbox
          images={galleryImages}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  );
}
